import type {
  BoneAssignmentDiagram,
  BoneAssignmentDiagramConnection,
  BoneAssignmentDiagramNode,
} from './types';

export type HandDiagramSide = 'left' | 'right';

export type HandTemplateRoleName =
  | 'hand'
  | 'indexMetacarpal'
  | 'middleMetacarpal'
  | 'ringMetacarpal'
  | 'pinkyMetacarpal'
  | 'thumb1'
  | 'thumb2'
  | 'thumb3'
  | 'index1'
  | 'index2'
  | 'index3'
  | 'middle1'
  | 'middle2'
  | 'middle3'
  | 'ring1'
  | 'ring2'
  | 'ring3'
  | 'pinky1'
  | 'pinky2'
  | 'pinky3';

export const HAND_TEMPLATE_ROLE_NAME_MAP: Readonly<
  Record<HandTemplateRoleName, HandTemplateRoleName>
> = {
  hand: 'hand',
  indexMetacarpal: 'indexMetacarpal',
  middleMetacarpal: 'middleMetacarpal',
  ringMetacarpal: 'ringMetacarpal',
  pinkyMetacarpal: 'pinkyMetacarpal',
  thumb1: 'thumb1',
  thumb2: 'thumb2',
  thumb3: 'thumb3',
  index1: 'index1',
  index2: 'index2',
  index3: 'index3',
  middle1: 'middle1',
  middle2: 'middle2',
  middle3: 'middle3',
  ring1: 'ring1',
  ring2: 'ring2',
  ring3: 'ring3',
  pinky1: 'pinky1',
  pinky2: 'pinky2',
  pinky3: 'pinky3',
} as const;

export const HAND_DIAGRAM_LAYOUT = {
  minHeightPx: 200,
  maxWidthPx: 210,
  contentBounds: {
    left: 12,
    right: 88,
    top: 12,
    bottom: 90,
  },
} as const satisfies Pick<
  BoneAssignmentDiagram,
  'minHeightPx' | 'maxWidthPx' | 'contentBounds'
>;

const HAND_TEMPLATE_NODES: readonly (BoneAssignmentDiagramNode & {
  roleName: HandTemplateRoleName;
})[] = [
  { roleName: 'hand', shortLabel: 'Hand', x: 50, y: 90, size: 'md' },
  {
    roleName: 'indexMetacarpal',
    shortLabel: 'IM',
    x: 42,
    y: 72,
    size: 'xs',
  },
  {
    roleName: 'middleMetacarpal',
    shortLabel: 'MM',
    x: 50,
    y: 70,
    size: 'xs',
  },
  { roleName: 'ringMetacarpal', shortLabel: 'RM', x: 58, y: 72, size: 'xs' },
  {
    roleName: 'pinkyMetacarpal',
    shortLabel: 'PM',
    x: 68,
    y: 75,
    size: 'xs',
  },
  { roleName: 'thumb1', shortLabel: 'T1', x: 32, y: 74, size: 'sm' },
  { roleName: 'thumb2', shortLabel: 'T2', x: 25, y: 62, size: 'sm' },
  { roleName: 'thumb3', shortLabel: 'T3', x: 22, y: 50, size: 'sm' },
  { roleName: 'index1', shortLabel: 'I1', x: 39, y: 57, size: 'xs' },
  { roleName: 'index2', shortLabel: 'I2', x: 37, y: 40, size: 'xs' },
  { roleName: 'index3', shortLabel: 'I3', x: 35.5, y: 24, size: 'xs' },
  { roleName: 'middle1', shortLabel: 'M1', x: 50, y: 56, size: 'xs' },
  { roleName: 'middle2', shortLabel: 'M2', x: 50, y: 38, size: 'xs' },
  { roleName: 'middle3', shortLabel: 'M3', x: 50, y: 19, size: 'xs' },
  { roleName: 'ring1', shortLabel: 'R1', x: 61, y: 59, size: 'xs' },
  { roleName: 'ring2', shortLabel: 'R2', x: 63, y: 43, size: 'xs' },
  { roleName: 'ring3', shortLabel: 'R3', x: 64.5, y: 27, size: 'xs' },
  { roleName: 'pinky1', shortLabel: 'P1', x: 71, y: 65, size: 'xs' },
  { roleName: 'pinky2', shortLabel: 'P2', x: 74, y: 53, size: 'xs' },
  { roleName: 'pinky3', shortLabel: 'P3', x: 76, y: 40, size: 'xs' },
] as const;

const HAND_TEMPLATE_CONNECTIONS: readonly (BoneAssignmentDiagramConnection & {
  from: HandTemplateRoleName;
  to: HandTemplateRoleName;
})[] = [
  { from: 'hand', to: 'thumb1' },
  { from: 'thumb1', to: 'thumb2' },
  { from: 'thumb2', to: 'thumb3' },
  { from: 'hand', to: 'indexMetacarpal' },
  { from: 'indexMetacarpal', to: 'index1' },
  { from: 'index1', to: 'index2' },
  { from: 'index2', to: 'index3' },
  { from: 'hand', to: 'middleMetacarpal' },
  { from: 'middleMetacarpal', to: 'middle1' },
  { from: 'middle1', to: 'middle2' },
  { from: 'middle2', to: 'middle3' },
  { from: 'hand', to: 'ringMetacarpal' },
  { from: 'ringMetacarpal', to: 'ring1' },
  { from: 'ring1', to: 'ring2' },
  { from: 'ring2', to: 'ring3' },
  { from: 'hand', to: 'pinkyMetacarpal' },
  { from: 'pinkyMetacarpal', to: 'pinky1' },
  { from: 'pinky1', to: 'pinky2' },
  { from: 'pinky2', to: 'pinky3' },
] as const;

export function buildPrimaryHandRoleNameMap(
  side: HandDiagramSide,
): Record<HandTemplateRoleName, string> {
  const sidePrefix = side === 'left' ? 'Left' : 'Right';
  const handPrefix = side === 'left' ? 'L' : 'R';

  return {
    hand: `${sidePrefix}Hand`,
    indexMetacarpal: `${sidePrefix}IndexMetacarpal`,
    middleMetacarpal: `${sidePrefix}MiddleMetacarpal`,
    ringMetacarpal: `${sidePrefix}RingMetacarpal`,
    pinkyMetacarpal: `${sidePrefix}PinkyMetacarpal`,
    thumb1: `${sidePrefix}Thumb1`,
    thumb2: `${sidePrefix}Thumb2`,
    thumb3: `${sidePrefix}Thumb3`,
    index1: `${sidePrefix}Index1`,
    index2: `${sidePrefix}Index2`,
    index3: `${sidePrefix}Index3`,
    middle1: `${sidePrefix}Middle1`,
    middle2: `${sidePrefix}Middle2`,
    middle3: `${sidePrefix}Middle3`,
    ring1: `${sidePrefix}Ring1`,
    ring2: `${sidePrefix}Ring2`,
    ring3: `${sidePrefix}Ring3`,
    pinky1: `${sidePrefix}Pinky1`,
    pinky2: `${sidePrefix}Pinky2`,
    pinky3: `${sidePrefix}Pinky3`,
  };
}

export function buildHandDiagram({
  key,
  label,
  side,
  roleNameByTemplateRole,
}: {
  key: string;
  label: string;
  side: HandDiagramSide;
  roleNameByTemplateRole: Record<HandTemplateRoleName, string>;
}): BoneAssignmentDiagram {
  const nodes = buildHandNodes(side, roleNameByTemplateRole);
  const connections = HAND_TEMPLATE_CONNECTIONS.map((connection) => ({
    from: roleNameByTemplateRole[connection.from],
    to: roleNameByTemplateRole[connection.to],
  }));

  return rotateDiagram180(
    mirrorDiagram({
      key,
      label,
      ...HAND_DIAGRAM_LAYOUT,
      nodes,
      connections,
    }),
  );
}

function buildHandNodes(
  side: HandDiagramSide,
  roleNameByTemplateRole: Record<HandTemplateRoleName, string>,
): BoneAssignmentDiagramNode[] {
  return HAND_TEMPLATE_NODES.map((node) => ({
    ...node,
    roleName: roleNameByTemplateRole[node.roleName],
    shortLabel:
      node.roleName === 'hand'
        ? `${side === 'left' ? 'L' : 'R'}Hand`
        : node.shortLabel,
    x: side === 'right' ? 100 - node.x : node.x,
  }));
}

function mirrorNodes(
  nodes: readonly BoneAssignmentDiagramNode[],
): readonly BoneAssignmentDiagramNode[] {
  return nodes.map((node) => ({
    ...node,
    x: 100 - node.x,
  }));
}

function rotateNodes180(
  nodes: readonly BoneAssignmentDiagramNode[],
): readonly BoneAssignmentDiagramNode[] {
  return nodes.map((node) => ({
    ...node,
    x: 100 - node.x,
    y: 100 - node.y,
  }));
}

function mirrorDiagram(diagram: BoneAssignmentDiagram): BoneAssignmentDiagram {
  return {
    ...diagram,
    nodes: mirrorNodes(diagram.nodes),
  };
}

function rotateDiagram180(diagram: BoneAssignmentDiagram): BoneAssignmentDiagram {
  return {
    ...diagram,
    nodes: rotateNodes180(diagram.nodes),
  };
}
